import {getBackendCurrencyValue, getDateFromIsoString} from '@shared/api';
import {currencyValueDivider} from '@shared/constants';
import {
  DepositDetails,
  PaymentMethod,
  PayoutDetails,
  ProviderRequestLog,
  Transaction,
} from '../types';
import {
  DepositDetailsOnBackend,
  PayoutDetailsOnBackend,
  ProviderRequestLogOnBackend,
  TransactionOnBackend,
} from './backendTypes';

export const mapTransactionFromBackend = ({
  id,
  user_id,
  real_amount,
  promo_amount,
  coins_amount,
  real_amount_local,
  promo_amount_local,
  currency,
  method,
  created_at,
  type,
  related_entity,
  related_entity_id,
  label,
  payment_type,
  txn_type,
  event_id,
  meta,
}: TransactionOnBackend): Transaction => ({
  id,
  userId: user_id,
  value: real_amount / currencyValueDivider,
  bonus: promo_amount / currencyValueDivider,
  localValue: getBackendCurrencyValue(real_amount_local),
  localBonus: getBackendCurrencyValue(promo_amount_local),
  currency: currency ?? '',
  method: method
    ? {
        name: method.name,
        currency: method.currency,
        provider: method.provider,
        country: method.country ?? null,
      }
    : null,
  coins: coins_amount ?? 0,
  created: getDateFromIsoString(created_at),
  assetType: type ?? '',
  txnType: txn_type ?? '',
  relatedEntity: related_entity ?? '',
  relatedEntityId: related_entity_id ?? '',
  label: label ? label.toLowerCase() : '',
  paymentType: payment_type ?? '',
  eventId: event_id ?? '',
  meta: meta ?? [],
});

const mapCurrencyValue = (value: number): number =>
  value / currencyValueDivider;

const mapOptionalCurrencyValue = (
  value: number | null | undefined,
): number | null =>
  value === null || value === undefined ? null : mapCurrencyValue(value);

const mapOptionalDate = (value: string | null | undefined): Date | null =>
  value ? getDateFromIsoString(value) : null;

type MethodOnBackend = DepositDetailsOnBackend['method'];

const mapPaymentMethodFromBackend = (
  method: MethodOnBackend,
): PaymentMethod | null =>
  method
    ? {
        id: method.id,
        name: method.name,
        currency: method.currency,
        provider: method.provider,
        tokenCode: method.token_code,
      }
    : null;

export const mapDepositDetailsFromBackend = ({
  deposit_id,
  user_id,
  status,
  amount,
  fee,
  fee_compensation,
  fiat_amount,
  amount_fiat_received,
  rate_snapshot,
  tracker_id,
  redirect_url,
  promo_code,
  promo_percent,
  promo_max_bonus,
  created_at,
  completed_at,
  failure_code,
  failure_detail,
  method,
}: DepositDetailsOnBackend): DepositDetails => ({
  id: deposit_id,
  userId: user_id,
  status,
  amount: mapCurrencyValue(amount),
  fee: mapCurrencyValue(fee),
  feeCompensation: mapCurrencyValue(fee_compensation),
  fiatAmount: mapOptionalCurrencyValue(fiat_amount),
  amountFiatReceived: mapOptionalCurrencyValue(amount_fiat_received),
  rateSnapshot: rate_snapshot ?? '',
  trackerId: tracker_id ?? '',
  redirectUrl: redirect_url ?? '',
  promoCode: promo_code ?? '',
  promoPercent: promo_percent ?? null,
  promoMaxBonus: mapOptionalCurrencyValue(promo_max_bonus),
  created: mapOptionalDate(created_at),
  completed: mapOptionalDate(completed_at),
  failureCode: failure_code ?? '',
  failureDetail: failure_detail ?? '',
  method: mapPaymentMethodFromBackend(method),
});

export const mapPayoutDetailsFromBackend = ({
  payout_id,
  user_id,
  status,
  amount,
  fee,
  fee_compensation,
  fiat_amount,
  amount_fiat_sent,
  rate_snapshot,
  tracker_id,
  receiver,
  created_at,
  completed_at,
  failure_code,
  failure_detail,
  method,
}: PayoutDetailsOnBackend): PayoutDetails => ({
  id: payout_id,
  userId: user_id,
  status,
  amount: mapCurrencyValue(amount),
  fee: mapCurrencyValue(fee),
  feeCompensation: mapCurrencyValue(fee_compensation),
  fiatAmount: mapOptionalCurrencyValue(fiat_amount),
  amountFiatSent: mapOptionalCurrencyValue(amount_fiat_sent),
  rateSnapshot: rate_snapshot ?? '',
  trackerId: tracker_id ?? '',
  receiver: receiver ?? '',
  created: mapOptionalDate(created_at),
  completed: mapOptionalDate(completed_at),
  failureCode: failure_code ?? '',
  failureDetail: failure_detail ?? '',
  method: mapPaymentMethodFromBackend(method),
});

export const mapProviderRequestLogFromBackend = ({
  id,
  provider,
  direction,
  entity_type,
  endpoint,
  method,
  url,
  status_code,
  created_at,
  request_body,
  response_body,
}: ProviderRequestLogOnBackend): ProviderRequestLog => ({
  id,
  provider,
  direction,
  entityType: entity_type,
  endpoint,
  method,
  url,
  statusCode: status_code ?? null,
  created: getDateFromIsoString(created_at),
  requestBody: request_body ?? null,
  responseBody: response_body ?? null,
});
